import React from 'react';
import { StickyComparisonHeader } from "./header/StickyComparisonHeader";
import { WorkspaceHeader } from "./header/WorkspaceHeader";
import { TabsNavigation } from "./header/TabsNavigation";
import { OrganizeActions } from "./header/OrganizeActions";
import { SummaryStats, EnhancedRoomMatch, OrganizedRoom } from "./types";
import { ComparisonFilters } from "@/hooks/useComparisonState";

interface ComparisonHeaderProps {
    claimId?: number;
    claimNumber?: string;
    comparisonId: number;
    isProcessing?: boolean;
    activeTab: string;
    enhancedMatchesCount: number;
    matchGroupsCount: number;
    stats: SummaryStats;
    filters: ComparisonFilters;
    enhancedMatches: EnhancedRoomMatch[];
    selectedItemIds: Set<number>;
    selectedInsurerCount: number;
    selectedContractorCount: number;
    sortedRooms: OrganizedRoom[];
    onNavigateBack: () => void;
    onSearchChange: (query: string) => void;
    onAddSearchTerm: (term?: string) => void;
    onRemoveSearchTerm: (term: string) => void;
    onClearSearchTerms: () => void;
    onToggleShowMatched: () => void;
    onToggleHideEmptyRooms: () => void;
    onSetConfidenceLevel: (level: string) => void;
    onApproveHighConfidence: () => void;
    onToggleShowOverscoped?: () => void;
    onToggleShowAdditional?: () => void;
    onToggleShowDiscrepancy?: () => void;
    onToggleShowNotOnInsurance?: () => void;
    onSetSpecialFilter?: (filter: 'overscoped' | 'additional' | 'discrepancy' | 'notOnInsurance' | 'none') => void;
    onMoveToRoom: (itemIds: number[], targetRoomId: number) => void;
    onCreateRoom: (type: 'insurer' | 'contractor') => void;
    onClearSelection: () => void;
}

export function ComparisonHeader({
    claimId,
    claimNumber,
    comparisonId,
    isProcessing,
    activeTab,
    enhancedMatchesCount,
    matchGroupsCount,
    stats,
    filters,
    selectedItemIds,
    selectedInsurerCount,
    selectedContractorCount,
    sortedRooms,
    onNavigateBack,
    onSearchChange,
    onAddSearchTerm,
    onRemoveSearchTerm,
    onClearSearchTerms,
    onToggleShowMatched,
    onToggleHideEmptyRooms,
    onSetConfidenceLevel,
    onToggleShowOverscoped,
    onToggleShowAdditional,
    onToggleShowDiscrepancy,
    onToggleShowNotOnInsurance,
    onSetSpecialFilter,
    onApproveHighConfidence,
    onMoveToRoom,
    onCreateRoom,
    onClearSelection,
}: ComparisonHeaderProps) {
    return (
        <div className="sticky top-0 z-40 border-b bg-slate-50/95 pt-3 pb-2 shadow-sm backdrop-blur-sm max-md:z-30 max-md:pt-2 dark:bg-slate-950/95">
            <div className="max-w-7xl mx-auto px-6 space-y-3 max-sm:px-1 max-sm:space-y-2">
                <WorkspaceHeader
                    claimId={claimId}
                    claimNumber={claimNumber}
                    comparisonId={comparisonId}
                    isProcessing={isProcessing}
                    onNavigateBack={onNavigateBack}
                />

                <TabsNavigation
                    enhancedMatchesCount={enhancedMatchesCount}
                    matchGroupsCount={matchGroupsCount}
                />

                {activeTab !== 'summary' && (
                    <StickyComparisonHeader
                        className="border-none shadow-none bg-transparent pt-0 pb-0"
                        top="static"
                        stats={stats}
                        filters={filters}
                        onSearchChange={onSearchChange}
                        onAddSearchTerm={onAddSearchTerm}
                        onRemoveSearchTerm={onRemoveSearchTerm}
                        onClearSearchTerms={onClearSearchTerms}
                        onToggleShowMatched={onToggleShowMatched}
                        onToggleHideEmptyRooms={onToggleHideEmptyRooms}
                        onSetConfidenceLevel={onSetConfidenceLevel}
                        onToggleShowOverscoped={onToggleShowOverscoped}
                        onToggleShowAdditional={onToggleShowAdditional}
                        onToggleShowDiscrepancy={onToggleShowDiscrepancy}
                        onToggleShowNotOnInsurance={onToggleShowNotOnInsurance}
                        onSetSpecialFilter={onSetSpecialFilter}
                        hideConfidenceFilters={activeTab === 'organize'}
                        onApproveHighConfidence={onApproveHighConfidence}
                    />
                )}

                {activeTab === 'organize' && (
                    <div className="pt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                        <OrganizeActions
                            selectedItemIds={selectedItemIds}
                            selectedInsurerCount={selectedInsurerCount}
                            selectedContractorCount={selectedContractorCount}
                            sortedRooms={sortedRooms}
                            onMoveToRoom={onMoveToRoom}
                            onCreateRoom={onCreateRoom}
                            onClearSelection={onClearSelection}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
